import { APICurrentUser, PersonalSetting } from '@ardoq/api-types';
import { BehaviorSubject } from 'rxjs';
import { EphemeralNotificationKeys } from './types';
import { EPHEMERAL_NOTIFICATION_KEYS } from './ephemeralNotificationConfig';
import { EphemeralNotificationRecords } from './ephemeralNotificationsStateOperations';
import { canAccessEphemeralNotificationsDevTool } from './utils';

/** Ardoq-admin-employee-only, per-session overrides letting them re-show or force-hide a notification without touching the backend. */
export type DevEphemeralNotificationOverrides = Partial<
  Record<EphemeralNotificationKeys, boolean>
>;

const SESSION_STORAGE_KEY = 'DEV_EPHEMERAL_NOTIFICATION_OVERRIDES';

const isKnownOverrideEntry = (
  entry: [string, unknown]
): entry is [EphemeralNotificationKeys, boolean] =>
  typeof entry[1] === 'boolean' &&
  EPHEMERAL_NOTIFICATION_KEYS.includes(entry[0] as EphemeralNotificationKeys);

const parseOverrides = (raw: unknown): DevEphemeralNotificationOverrides =>
  typeof raw !== 'object' || raw === null || Array.isArray(raw)
    ? {}
    : Object.fromEntries(Object.entries(raw).filter(isKnownOverrideEntry));

const loadFromSessionStorage = (): DevEphemeralNotificationOverrides => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      return parseOverrides(JSON.parse(stored));
    }
  } catch {
    // Ignore parse errors
  }
  return {};
};

const saveToSessionStorage = (
  overrides: DevEphemeralNotificationOverrides
): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // Ignore storage errors
  }
};

const devOverridesSubject =
  new BehaviorSubject<DevEphemeralNotificationOverrides>(
    loadFromSessionStorage()
  );

export const devEphemeralNotificationOverrides$ =
  devOverridesSubject.asObservable();

export const setDevEphemeralNotificationOverride = (
  currentUser: APICurrentUser,
  notificationKey: EphemeralNotificationKeys,
  isResolved: boolean
): void => {
  if (!canAccessEphemeralNotificationsDevTool(currentUser)) return;
  const updated = {
    ...devOverridesSubject.getValue(),
    [notificationKey]: isResolved,
  };
  saveToSessionStorage(updated);
  devOverridesSubject.next(updated);
};

/** Woodpecker devtool: Force-hide upserts a resolved record; show-again drops it entirely. */
const applyOverrideToRecords = (
  records: EphemeralNotificationRecords,
  notificationKey: string,
  isResolved: boolean,
  now: Date
): EphemeralNotificationRecords => {
  if (!isResolved) {
    return Object.fromEntries(
      Object.entries(records).filter(([key]) => key !== notificationKey)
    );
  }
  const existingRecord = records[notificationKey];
  return {
    ...records,
    [notificationKey]: {
      firstSeenAt: existingRecord?.firstSeenAt ?? now.toISOString(),
      lastCountedViewAt: existingRecord?.lastCountedViewAt,
      viewCount: existingRecord?.viewCount ?? 0,
      outcome: { status: 'dismissed' },
    },
  };
};

export const applyDevEphemeralNotificationOverrides = (
  user: APICurrentUser,
  overrides: DevEphemeralNotificationOverrides,
  now: Date
): APICurrentUser => {
  if (Object.keys(overrides).length === 0 || !user.settings) {
    return user;
  }

  // Also drop show-again keys here, or the legacy-ledger fallback in
  // `getEphemeralNotificationRecords` would resynthesize the record just removed.
  const dismissedKeys = new Set(
    user.settings[PersonalSetting.EPHEMERAL_NOTIFICATION_DISMISSED_KEYS] ?? []
  );
  Object.entries(overrides).forEach(([notificationKey, isResolved]) => {
    if (!isResolved) dismissedKeys.delete(notificationKey);
  });

  const records = Object.entries(overrides).reduce(
    (accRecords, [notificationKey, isResolved]) =>
      applyOverrideToRecords(accRecords, notificationKey, isResolved, now),
    user.settings[PersonalSetting.EPHEMERAL_NOTIFICATIONS_STATE]?.records ?? {}
  );

  return {
    ...user,
    settings: {
      ...user.settings,
      [PersonalSetting.EPHEMERAL_NOTIFICATION_DISMISSED_KEYS]: [
        ...dismissedKeys,
      ],
      [PersonalSetting.EPHEMERAL_NOTIFICATIONS_STATE]: { records },
    },
  };
};
